<script setup lang="ts">
    import {computed, ref} from 'vue';
    import ChatCheckmarkIcon from '../ui/ChatCheckmarkIcon.vue';

    const props = defineProps<{
        products: Record<string, any>[];
        meta: Record<string, any>;
        disabled: boolean;
    }>();

    const emit = defineEmits<{(event: 'compare', productIds: string[]): void}>();

    const shownProducts = computed(() => props.products);
    const shown = computed(() => (Number.isInteger(props.meta?.shown) ? props.meta.shown : props.products.length));
    const total = computed(() => (Number.isInteger(props.meta?.total) ? props.meta.total : props.products.length));

    const selected = ref<number[]>([]);

    function name(product: Record<string, any>): string {
        return product.title || product.slug || 'Product';
    }

    function price(product: Record<string, any>): string {
        if (product.price === null || product.price === undefined) {
            return 'Prijs op aanvraag';
        }

        const number = Number(product.price);

        return Number.isNaN(number) ? `EUR ${product.price}` : number.toLocaleString('nl-NL', {style: 'currency', currency: 'EUR'});
    }

    /** "Op voorraad · morgen in huis" - the delivery half only when the mapper produced one. */
    function stockLine(product: Record<string, any>): string {
        return product.delivery ? `Op voorraad · ${product.delivery}` : 'Op voorraad';
    }

    // Keep the explicit selection as product IDs: article numbers can repeat across brands.
    function compare(): void {
        emit(
            'compare',
            [...selected.value].sort((a, b) => a - b).map((index) => shownProducts.value[index].productId)
        );
    }
</script>

<template>
    <div class="products">
        <div
            v-for="(product, index) in shownProducts"
            :key="index"
            class="panel panel-default mb-0 product">
            <!-- The list page's badge markup, so the shop's own discount-badge styles draw it. -->
            <div
                v-if="product.discount > 0"
                class="discount-badge product__discount">
                <dl class="discount-badge__inner">
                    <dt>-{{ product.discount }}%</dt>
                    <dd>&nbsp;</dd>
                </dl>
            </div>
            <img
                v-if="product.image"
                class="product__image"
                :src="product.image"
                :alt="name(product)"
                loading="lazy"
                decoding="async" />

            <div class="product__info">
                <div
                    v-if="product.brand"
                    class="product__brand text-uppercase text-muted">
                    {{ product.brand }}
                </div>
                <div class="product__title">{{ name(product) }}</div>

                <div
                    v-if="product.inStock"
                    class="product__stock">
                    <ChatCheckmarkIcon
                        variant="outline"
                        :size="14" />
                    <span>{{ stockLine(product) }}</span>
                </div>
                <div
                    v-else
                    class="product__stock product__stock--out">
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </svg>
                    <span>Tijdelijk uitverkocht</span>
                </div>

                <label
                    v-if="shownProducts.length > 1"
                    class="product__compare text-muted">
                    <input
                        v-model="selected"
                        type="checkbox"
                        :value="index"
                        :disabled="disabled" />
                    Vergelijken
                </label>
            </div>

            <div class="product__buy">
                <div class="product__price">{{ price(product) }}</div>
                <!--
                    The design's "Bestellen" button adds the part to the cart from inside the chat.
                    There is no add-to-cart path in the chat pipeline, so this stays a link to the
                    product page - where the site's own add-to-cart lives. Wiring the real thing
                    means calling the shopping-cart endpoint (see AddShoppingItem.vue) with a
                    quantity, and deciding what the chat shows afterwards.
                -->
                <a
                    v-if="product.url"
                    class="c-button product__order"
                    :href="product.url"
                    target="_blank"
                    rel="noopener">
                    Details
                </a>
            </div>
        </div>

        <div
            v-if="selected.length >= 2"
            class="products__compare-bar">
            <button
                type="button"
                class="black mao-button"
                :disabled="disabled"
                @click="compare">
                Vergelijk ({{ selected.length }})
            </button>
        </div>

        <div
            v-if="total > shown"
            class="products__meta text-muted">
            Er zijn in totaal {{ total }} resultaten beschikbaar.
            <!--
                The design links on to "Bekijk alle <categorie> voor deze auto" and a cross-sell
                ("Heb ik ook remblokken nodig?"). Neither is in the turn payload: productMeta
                carries categoryId/total/appliedFilters but no list-page URL, and nothing produces
                a related-part suggestion. Both need a field on the search result before they can
                be links rather than guesses.
            -->
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '../../../sass/globals';
    .products {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
    }

    .product {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        min-width: 0;
        padding: 10px;
        position: relative;
        border-radius: 0;
    }

    /* The shop only positions .discount-badge inside .mao-product-fragment. */
    .product__discount {
        position: absolute;
        top: 6px;
        right: 6px;
        z-index: 1;
    }

    /* A fixed 72px height next to a card whose width already changes (3-up desktop vs. the
       minmax(200px, 1fr) mobile scroller) doesn't scale with it. aspect-ratio keeps the
       thumbnail's shape fixed while its size follows the card's own width; max-height only
       stops it growing past a sensible size on a very wide card. */
    .product__image {
        align-self: center;
        flex: 0 0 auto;
        width: 100%;
        aspect-ratio: 1;
        max-height: 96px;
        object-fit: contain;
        border-radius: 4px;
    }

    .product__info {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-width: 0;
    }

    .product__brand {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.06em;
    }

    .product__title {
        display: -webkit-box;
        min-height: calc(1.3em * 3);
        overflow: hidden;
        margin-top: 1px;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.3;
        color: $black;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
    }

    .product__stock {
        display: grid;
        grid-template-columns: 14px minmax(0, 1fr);
        align-items: start;
        gap: 6px;
        margin-top: 4px;
        font-size: 12px;
        font-weight: 700;
        line-height: 1.3;
        color: $green;
    }

    .product__stock--out {
        color: #a94442;
    }

    .product__compare {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        font-size: 12px;
        cursor: pointer;
    }

    .product__compare input {
        width: auto;
        margin: 0;
        cursor: pointer;
    }

    .product__compare input:disabled {
        cursor: default;
    }

    .product__buy {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        width: 100%;
        margin-top: auto;
    }

    .product__price {
        font-size: 17px;
        font-weight: 900;
        line-height: 1.2;
        color: $black;
    }

    /* Compact padding lets the shop button fit beside the price. */
    .chat .product__order {
        width: auto;
        padding: 0 12px;
        font-size: 13px;
        color: $black;
    }

    .products__compare-bar {
        grid-column: 1 / -1;
        margin-top: 2px;
        .mao-button {
            width: auto;
            font-size: 14px;
        }
    }

    .products__meta {
        grid-column: 1 / -1;
        font-size: 13px;
    }

    @media (max-width: $chat-mobile) {
        .products {
            grid-template-columns: repeat(3, minmax(200px, 1fr));
            padding-bottom: 4px;
            overflow-x: auto;
            scroll-snap-type: x proximity;
        }

        .product {
            scroll-snap-align: start;
        }
    }
</style>
